import { notify } from '@billy/design-system'
import React, { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { NotificationKeys } from '../../../../enums/notificationKeys'
import { useEmberTransitionTo } from '../../../../hooks/routing/useEmberTransitionTo'
import { SubmitLoginResponseData } from '../../../../query-api/login-query'
import { SubmitSignupResponseData } from '../../../../query-api/signup-query'
import { APIError, setAccessToken } from '../../../../utils'
import { getOrganizationViewUrl } from '../../../../utils/routing/getOrganizationViewUrl'
import { reactRoute } from '../../../../utils/routing/reactRoute'
import { useOrganizationInvitation } from '../../context/organizationInvitationContext'
import { acceptOrganizationInvitation } from '../../query-api'
import { Layout } from '../Layout'
import { LoginForm } from '../LoginForm'
import { SignupForm } from '../SignupForm'
import * as Styled from './styles'

interface AcceptOrganizationInvitationProps {
  existingUser: boolean
  invitationEmail: string
  invitationToken: string
  organizationName: string
  organizationUrl: string
}

export const AcceptOrganizationInvitation = ({
  existingUser,
  invitationEmail,
  invitationToken,
  organizationName,
  organizationUrl,
}: AcceptOrganizationInvitationProps): ReactElement => {
  const { t } = useTranslation()
  const { authorizeEmberUser, registerEmberOrganization } = useOrganizationInvitation()
  const handleError = useCallback(() => {
    notify({ id: NotificationKeys.LoginError, message: t('error_saving'), variant: 'error' })
  }, [t])
  const { transitionTo } = useEmberTransitionTo()
  const dashboardUrl = getOrganizationViewUrl(organizationUrl, reactRoute.organization.dashboard.getMainRoute())

  const acceptInvitationOnLogin = useCallback(
    async ({ meta: { accessToken } }: SubmitLoginResponseData) => {
      // Authorize the current user by access token
      setAccessToken(accessToken)
      authorizeEmberUser(accessToken)

      // Accept the invitation by invitation's token
      try {
        await acceptOrganizationInvitation(invitationToken)
      } catch (error: unknown) {
        // Handle custom error message on invalid type of invitation
        // This needs to live here, because server always returns a string in english, and we need to translate it
        // to the user's language (as abstract error handling logic doesn't do translations, just display generic error message)
        if (error instanceof APIError && error.type === 'E_INCOMPATIBLE_INVITATION') {
          error.validationErrors = {
            credentials: { message: t('organization_invitation.form.errors.incompatible_invitation') },
          }
        }

        throw error
      }

      // Register the current user organization in the Ember
      registerEmberOrganization({
        organizationUrl,
        onSuccess: () => {
          if (dashboardUrl) {
            transitionTo(dashboardUrl)
          }
        },
        onError: handleError,
      })
    },
    [
      authorizeEmberUser,
      dashboardUrl,
      handleError,
      invitationToken,
      registerEmberOrganization,
      organizationUrl,
      transitionTo,
      t,
    ],
  )

  const acceptInvitationOnSignup = useCallback(
    async ({ meta: { accessToken } }: SubmitSignupResponseData) => {
      // Authorize the current user by access token
      setAccessToken(accessToken)
      authorizeEmberUser(accessToken)

      // Register the current user organization in the Ember
      registerEmberOrganization({
        organizationUrl,
        onSuccess: () => {
          if (dashboardUrl) {
            transitionTo(dashboardUrl)
          }
        },
        onError: handleError,
      })
    },
    [authorizeEmberUser, dashboardUrl, handleError, registerEmberOrganization, organizationUrl, transitionTo],
  )

  return (
    <Layout withFooter>
      <Styled.AcceptInvitationFormWrapper>
        {existingUser ? (
          <LoginForm
            invitationEmail={invitationEmail}
            onSuccess={acceptInvitationOnLogin}
            title={t('organization_invitation.form.title', { organizationName })}
          />
        ) : (
          <SignupForm
            invitationEmail={invitationEmail}
            invitationToken={invitationToken}
            onSuccess={acceptInvitationOnSignup}
            title={t('organization_invitation.form.title', { organizationName })}
          />
        )}
      </Styled.AcceptInvitationFormWrapper>
    </Layout>
  )
}
